import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DomainBaseComponent, wrapBusy } from "@/shared/domain-base.component";
import { DomainService } from "@/shared/domain.service";
import { PendingPoCancelRequest, PoCancelRequestQueueService } from "./po-cancel-request-queue.service";

interface ManufacturerGroup {
  manufacturerId: number;
  manufacturerName: string;
  rows: PendingPoCancelRequest[];
}

@Component({
  selector: "app-pending-cancellations-tray",
  templateUrl: "./pending-cancellations-tray.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PendingCancellationsTrayComponent extends DomainBaseComponent implements OnInit {
  expanded = false;
  pending: PendingPoCancelRequest[] = [];
  groups: ManufacturerGroup[] = [];
  loading = false;

  private readonly reload$ = new Subject<void>();

  constructor(
    protected override domainService: DomainService,
    private queueService: PoCancelRequestQueueService,
  ) {
    super(domainService);
  }

  override async ngOnInit() {
    super.ngOnInit();
    await this.refresh();

    this.queueService.refreshRequested$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.refresh());
  }

  get totalCount(): number {
    return this.pending.length;
  }

  get manufacturerCount(): number {
    return this.groups.length;
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    if (this.expanded) this.refresh();
  }

  @wrapBusy
  async refresh() {
    this.loading = true;
    try {
      this.pending = await this.queueService.getPending();
      this.groups = _.chain(this.pending)
        .groupBy((r) => r.ManufacturerId)
        .map(
          (rows, mfrId): ManufacturerGroup => ({
            manufacturerId: Number(mfrId),
            manufacturerName: rows[0].ManufacturerName,
            rows,
          }),
        )
        .sortBy((g) => g.manufacturerName.toLowerCase())
        .value();
    } finally {
      this.loading = false;
    }
  }

  @wrapBusy
  async flushNow() {
    const result = await this.queueService.flush();
    if (result.EmailsSent === 0) {
      this.toastr.info("Nothing pending to send.");
    } else {
      this.toastr.success(
        `Sent ${result.EmailsSent} manufacturer email${result.EmailsSent === 1 ? "" : "s"}.`,
      );
    }
    await this.refresh();
  }
}
